import {firebaseApp} from "../config/firebase"
import { getFirestore, doc, writeBatch, serverTimestamp, query, orderBy, limit, collection, where, onSnapshot, updateDoc} from "firebase/firestore"
import { logError } from "../utils/errorHandlingUtils"
import { TICKET_STATUS_CREATED } from "../constants/ticket"

export const CREATE_TICKET = 'CREATE_TICKET'
export const SAVE_TICKETS = 'SAVE_TICKETS'
export const VERIFY_TICKET = "VERIFY_TICKET"

export const saveTickets = tickets => {
    return {
        type: SAVE_TICKETS,
        payload: {
            tickets
        }
    }
}

export const createTicket = (ticket) => {
    return {
        type: CREATE_TICKET,
        payload: {
            ticket
        }
    }
}

export const verifyTicket = (
    ticketId, 
    verification
) => {
    return {
        type: VERIFY_TICKET,
        payload: {
            ticketId,
            verification
        }
    }
}

export const fetchBatchCreateTicketsForPayeeCatalog = (
    tickets=[],
    ticketBatchId,
    payeeCatalogId,
    payeeId,
    ticketCount,
    startingTicketNumber,
    endingTicketNumber,
    ticketStyle={},
    onSuccess=()=>{}, 
    onError=()=>{}
 ) => {
    if (tickets.length === 0) return false
    const firestore = getFirestore(firebaseApp);
    return async (dispatch, getState) => {
        try{
            const {user} = getState()
            const now = Date.now();
            const batchArray = [writeBatch(firestore)]
            const batchLimit = 400 //after this many operations on the batch, a new batch will be added and used
            let batchIndex = 0
            let operationCount = 0
            for(let i = 0; i < tickets.length; i++){
                const batch = batchArray[batchIndex]
                const ticket = {...tickets[i]}
                ticket.createdAt = now
                ticket.createdAtTimestamp = serverTimestamp()
                ticket.createdByUserId = user.id
                ticket.currentStatus = TICKET_STATUS_CREATED
                ticket.statusHistory = {[TICKET_STATUS_CREATED]: now}
                const ticketRef = doc(firestore, "tickets", ticket.id)
                batch.set(ticketRef, ticket)
                operationCount++ 
                if (operationCount >= batchLimit) {
                    batchArray.push(writeBatch(firestore));
                    batchIndex++; //move to the next batch
                    operationCount = 0; //reset the op counter
                }
            }
            //write a ticket batch to store metadata about the creation of this batch of tickets
            const ticketBatch = {
                id: ticketBatchId,
                payeeCatalogId,
                payeeId,
                ticketCount,
                startingTicketNumber,
                endingTicketNumber,
                ticketStyle:{
                    ...ticketStyle,
                    backgroundImage: ""
                },
                createdByUserId: user.id,
                createdAt: now,
                createdAtTimestamp: serverTimestamp()
            }
            const lastBatch = batchArray[batchArray.length - 1]
            const ticketBatchRef = doc(firestore, `payeeCatalogs/${payeeCatalogId}/ticketBatches`, ticketBatchId)
            lastBatch.set(ticketBatchRef, ticketBatch)
            // Commit all batches
            await Promise.all(batchArray.map((batch) => batch.commit()));
            //save tickets in redux
            tickets.forEach(ticket => {
                dispatch(createTicket(ticket))
            })
            onSuccess(tickets)
            return true
        } catch (e){
            const message = `action > tickets > fetchBatchCreateTicketsForPayeeCatalog: Failed to batch create tickets for payee catalog ${payeeCatalogId} ${JSON.stringify(tickets)}`
            if (e.message_){
                //deal with firebase-specific errors
                logError(new Error(`${e.message} ${message}`))
            } else {
                e.message = `${e.message} ${message}`
                logError(e)
            }
            onError(tickets)
            return false
        }
        
    }
}

export const fetchSubscribeToLastTicketNumberForPayeeCatalog = (
    payeeCatalogId,
    payeeId,
    onSuccess = () => {}, 
    onError = () => {}
) => {
    try {
        const firestore = getFirestore(firebaseApp);
        const batchesRef = collection(firestore, `payeeCatalogs/${payeeCatalogId}/ticketBatches`);
        const ticketBatchesRef = query(batchesRef,
                                        where("payeeId", "==", payeeId), 
                                        orderBy("endingTicketNumber", "desc"), limit(1));
        return async (dispatch) => {
            const lastTicketBatchListener = await onSnapshot(ticketBatchesRef, 
                querySnapshot => {
                    let lastTicketNumber = 0;
                    if (!querySnapshot.empty) {
                        const highestBatch = querySnapshot.docs[0].data();
                        if (typeof highestBatch.endingTicketNumber === "number") {
                            lastTicketNumber = highestBatch.endingTicketNumber;
                        }
                    }
                    onSuccess(lastTicketNumber);
                }
            );

            return lastTicketBatchListener; // caller can use this to stop listening
        }
    } catch (e) {
        const message = `action > tickets > fetchSubscribeToLastTicketNumberForPayeeCatalog: Failed to find the highest ticket number from ticket batches for payee catalog ${payeeCatalogId}`;
        if (e.message_) {
            logError(new Error(`${e.message} ${message}`));
        } else {
            e.message = `${e.message} ${message}`;
            logError(e);
        }
        onError();
        return () => {}; // return no-op unsubscribe on failure
    }
};

export const fetchSubscribeToTicketsForPayeeCatalog = ( //TODO change to payee catalog id
    payeeCatalogId,
    payeeId,
 ) => {
    const firestore = getFirestore(firebaseApp)
    const ticketsRef = query(
                            collection(firestore, "tickets"), 
                            where("payeeCatalogId", "==", payeeCatalogId),
                            where("payeeId", "==", payeeId), 
                        )
    return async (dispatch) => {
        try{
            const ticketsListener = await onSnapshot(ticketsRef,
                querySnapshot =>{
                    //get an array of tickets from the snapshot
                    const tickets = querySnapshot.docs.map(docRef => ({...docRef.data()}));
                    dispatch(saveTickets(tickets))
                }
            )
            return ticketsListener
        } catch (e){
            const message = `action > tickets > fetchSubscribeToTicketsForPayee: Failed to save tickets for ${payeeId}`
            if (e.message_){
                //deal with firebase-specific errors
                logError(new Error(`${e.message} ${message}`))
            } else {
                e.message = `${e.message} ${message}`
                logError(e)
            }
            return () => {}
        }
    }
}

export const fetchVerifyTicket = (ticketId, verificationId) => {
    /**
      * Purpose: mark a ticket as verified
      */

    const firestore = getFirestore(firebaseApp)
    const ticketRef = doc(firestore, "tickets", ticketId)
                                
    return async (dispatch, getState) => {
        try {
            const {user} = getState()
            const verification = {
                id: verificationId,
                verifiedByUserId: user.id,
                verifiedAt: Date.now(),
                quantity: 1
            }
            updateDoc(ticketRef, {
                [`verificationsById.${verification.id}`]: verification,
                lastUpdatedAtTimestamp: serverTimestamp(),
                lastUpdatedAt: Date.now(),
                lastUpdatedByUserId: user.id
            })
            dispatch(verifyTicket(ticketId, verification))
            return true
        } catch (e){
            const message = `action > tickets > fetchVerifyTicket: Failed to verify ticket ${ticketId}`
            if (e.message_){
                //deal with firebase-specific errors
                logError(new Error(`${e.message} ${message}`))
            } else {
                e.message = `${e.message} ${message}`
                logError(e)
            }
            return false
        }
    }
}